/**
 * Utility for practical kitchen unit conversions between US and Metric systems.
 * Follows "sensible" rounding rules common in culinary applications.
 */

const COMMON_TEMPS = {
  // US to Metric
  '325': 165,
  '350': 180,
  '375': 190,
  '400': 200,
  '425': 220,
  // Metric to US
  '160': 325,
  '165': 325,
  '170': 340,
  '175': 350,
  '180': 350,
  '190': 375,
  '200': 400,
  '220': 425
};

const parseNumeric = (str) => {
  if (!str) return 0;
  // Handle fractions like "1 1/2" or "1/2"
  if (str.includes('/')) {
    const parts = str.trim().split(/\s+/);
    if (parts.length === 2) {
      const [whole, frac] = parts;
      const [num, den] = frac.split('/');
      return parseFloat(whole) + (parseFloat(num) / parseFloat(den));
    } else {
      const [num, den] = parts[0].split('/');
      return parseFloat(num) / parseFloat(den);
    }
  }
  return parseFloat(str);
};

const formatUSFraction = (val) => {
  const whole = Math.floor(val);
  const remainder = val - whole;
  
  if (remainder < 0.1) return whole > 0 ? `${whole}` : '0';
  if (Math.abs(remainder - 0.25) < 0.05) return whole > 0 ? `${whole} 1/4` : '1/4';
  if (Math.abs(remainder - 0.33) < 0.05) return whole > 0 ? `${whole} 1/3` : '1/3';
  if (Math.abs(remainder - 0.5) < 0.05) return whole > 0 ? `${whole} 1/2` : '1/2';
  if (Math.abs(remainder - 0.66) < 0.05) return whole > 0 ? `${whole} 2/3` : '2/3';
  if (Math.abs(remainder - 0.75) < 0.05) return whole > 0 ? `${whole} 3/4` : '3/4';
  
  return val.toFixed(1).replace('.0', '');
};

export const convertText = (text, targetSystem) => {
  if (!text) return text;

  // Regex for numbers (fractions, decimals, integers) + units
  // Captures: "1 1/2", "0.5", "500" and units
  const regex = /(\d+(?:\s+\d+\/\d+)?|\d+\/\d+|\d+(?:\.\d+)?)\s*(cups?|tbsp|tsp|fl\s*oz|oz|g|ml|°C|°F)\b/gi;

  return text.replace(regex, (match, valStr, unit) => {
    const val = parseNumeric(valStr);
    const u = unit.toLowerCase();

    // TARGET: METRIC (From US)
    if (targetSystem === 'Metric') {
      if (u.startsWith('cup')) return `${Math.round(val * 240 / 10) * 10} ml`;
      if (u === 'tbsp') return `${val * 15} ml`;
      if (u === 'tsp') return `${val * 5} ml`;
      if (u === 'oz') return `${Math.round(val * 28)} g`;
      if (u === 'fl oz' || u === 'floz') return `${val * 30} ml`;
      if (u === '°f') {
        const temp = Math.round(val).toString();
        if (COMMON_TEMPS[temp]) return `${COMMON_TEMPS[temp]}°C`;
        return `${Math.round((val - 32) / 1.8 / 5) * 5}°C`;
      }
    }

    // TARGET: US (From Metric)
    if (targetSystem === 'US') {
      if (u === 'ml') {
        if (val >= 240) return `${formatUSFraction(val / 240)} cup${val / 240 > 1.1 ? 's' : ''}`;
        if (val >= 30) return `${Math.round(val / 30)} fl oz`;
        if (val >= 15) return `${Math.round(val / 15)} tbsp`;
        return `${Math.round(val / 5)} tsp`;
      }
      if (u === 'g') {
        const oz = val / 28;
        return `${oz < 1 ? formatUSFraction(oz) : Math.round(oz * 2) / 2} oz`;
      }
      if (u === '°c') {
        const temp = Math.round(val).toString();
        if (COMMON_TEMPS[temp]) return `${COMMON_TEMPS[temp]}°F`;
        return `${Math.round((val * 1.8 + 32) / 5) * 5}°F`;
      }
    }

    return match;
  });
};